import React, { useEffect, useState } from 'react';
import { FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import type { WizardData } from './index';

interface Step2XmlProps {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
}

export function Step2Xml({ data, onUpdate }: Step2XmlProps) {
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any>(null);

  useEffect(() => {
    if (data.xmlContent && !data.parsedXml) {
      parseXml(data.xmlContent);
    } else if (data.parsedXml) {
      setParsedData(data.parsedXml);
    }
  }, [data.xmlContent]);

  const parseXml = async (xmlContent: string) => {
    setIsParsing(true);
    setParseError(null);

    try {
      // Call Edge Function for server-side parsing
      const { data: result, error } = await supabase.functions.invoke('parse-nfe-xml', {
        body: { xmlContent }
      });

      if (error) {
        console.error("Edge Function error:", error);
        throw new Error(error.message || "Erro ao processar XML no servidor");
      }

      if (result.error) {
        throw new Error(result.error);
      }

      // Transform to expected format for the wizard
      const { supplier, invoice, items, totals, billing, payments } = result;

      const parsed = {
        ide: {
          nNF: invoice.number,
          serie: invoice.series,
          dhEmi: invoice.emissionDate,
          natOp: invoice.natureOfOperation,
        },
        emit: {
          CNPJ: supplier.cnpj,
          CPF: supplier.cpf,
          xNome: supplier.name,
          xFant: supplier.tradeName,
          IE: supplier.stateRegistration,
          enderEmit: supplier.address ? {
            xLgr: supplier.address.street,
            nro: supplier.address.number,
            xBairro: supplier.address.neighborhood,
            xMun: supplier.address.city,
            UF: supplier.address.state,
            CEP: supplier.address.zipCode,
          } : undefined,
        },
        dest: {},
        det: items.map((item: any) => ({
          prod: {
            cProd: item.code,
            xProd: item.description,
            NCM: item.ncm,
            CFOP: item.cfop,
            uCom: item.unit,
            qCom: item.quantity,
            vUnCom: item.unitPrice,
            vProd: item.totalPrice,
            cEAN: item.ean,
          },
        })),
        total: {
          ICMSTot: {
            vProd: totals.products,
            vFrete: totals.freight,
            vSeg: totals.insurance,
            vDesc: totals.discount,
            vOutro: totals.otherCosts,
            vIPI: totals.ipi,
            vICMS: totals.icms,
            vPIS: totals.pis,
            vCOFINS: totals.cofins,
            // R-COMPRAS.19: impostos que compõem o vNF além do IPI (parser já devolve os três;
            // paridade com ImportStep1Upload — tax_value = IPI + ST + DIFAL + FCP-ST)
            vST: totals.st || 0,
            vICMSUFDest: totals.icmsUfDest || 0,
            vFCPST: totals.fcpSt || 0,
            vNF: totals.total,
          },
        },
        transp: {},
        cobr: billing ? {
          fat: {
            nFat: billing.invoiceNumber,
            vOrig: billing.originalValue,
            vDesc: billing.discountValue,
            vLiq: billing.netValue,
          },
          dup: billing.installments?.map((dup: any) => ({
            nDup: dup.number,
            dVenc: dup.dueDate,
            vDup: dup.value,
          })),
        } : undefined,
        // T9-lite: a Edge devolve payments[] { paymentType (tPag), paymentValue (vPag),
        // paymentTypeDescription } (parse-nfe-xml:296-300; espelho vivo ImportStep1Upload:171-175).
        // getPaymentInfo lê pag.detPag[].tPag/vPag.
        pag: Array.isArray(payments) && payments.length > 0
          ? {
              detPag: payments.map((p: any) => ({
                tPag: p.paymentType,
                vPag: p.paymentValue,
              })),
            }
          : {},
        infAdic: {},
      };

      setParsedData(parsed);
      onUpdate({ parsedXml: parsed });
    } catch (error) {
      console.error('Error parsing XML:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar XML da nota fiscal';
      setParseError(errorMessage);
    } finally {
      setIsParsing(false);
    }
  };

  const getItemsCount = () => {
    if (!parsedData?.det) return 0;
    return Array.isArray(parsedData.det) ? parsedData.det.length : 1;
  };

  const getTotalValue = () => {
    if (!parsedData?.total?.ICMSTot) return data.invoice.total_value || 0;
    return parseFloat(parsedData.total.ICMSTot.vNF || '0');
  };

  const getPaymentInfo = () => {
    if (!parsedData?.pag) return null;
    
    const detPag = parsedData.pag.detPag;
    if (!detPag) return null;

    const payments = Array.isArray(detPag) ? detPag : [detPag];
    
    const paymentTypes: Record<string, string> = {
      '01': 'Dinheiro',
      '02': 'Cheque',
      '03': 'Cartão de Crédito',
      '04': 'Cartão de Débito',
      '05': 'Crédito Loja',
      '10': 'Vale Alimentação',
      '11': 'Vale Refeição',
      '12': 'Vale Presente',
      '13': 'Vale Combustível',
      '14': 'Duplicata Mercantil',
      '15': 'Boleto Bancário',
      '16': 'Depósito Bancário',
      '17': 'PIX',
      '18': 'Transferência Bancária',
      '19': 'Programa Fidelidade',
      '90': 'Sem Pagamento',
      '99': 'Outros',
    };

    return payments.map((p: any) => ({
      type: paymentTypes[p.tPag] || 'Outros',
      value: parseFloat(p.vPag || '0'),
    }));
  };

  const getDueInfo = () => {
    if (!parsedData?.cobr?.dup) return null;
    
    const dups = Array.isArray(parsedData.cobr.dup) 
      ? parsedData.cobr.dup 
      : [parsedData.cobr.dup];

    return dups.map((d: any) => ({
      number: d.nDup,
      dueDate: d.dVenc,
      value: parseFloat(d.vDup || '0'),
    }));
  };

  if (isParsing) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-2 text-sm text-muted-foreground">Processando XML...</p>
      </div>
    );
  }

  if (!data.xmlContent) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="h-12 w-12 text-yellow-500" />
        <h3 className="text-lg font-medium mt-4">XML completo ainda não disponível</h3>
        <p className="text-sm text-muted-foreground mt-2 text-center max-w-md">
          A SEFAZ entregou apenas o resumo desta nota. Dê <strong>ciência da emissão</strong> na
          lista de notas; o XML completo chega no próximo sync (até 1 hora). Depois, reabra esta
          importação.
        </p>
      </div>
    );
  }

  if (parseError) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <h3 className="text-lg font-medium mt-4">Erro ao processar XML</h3>
        <p className="text-sm text-muted-foreground mt-2">{parseError}</p>
      </div>
    );
  }

  const payments = getPaymentInfo();
  const dues = getDueInfo();

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">XML Importado com Sucesso</h3>
        <p className="text-sm text-muted-foreground">
          Confira os dados extraídos da nota fiscal
        </p>
      </div>

      {/* Success indicator */}
      <Alert className="bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800 dark:text-green-200">
          XML processado com sucesso. {getItemsCount()} item(ns) encontrado(s).
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Invoice Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Dados da Nota
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Número</span>
                <p className="font-medium">{parsedData?.ide?.nNF || data.invoice.invoice_number}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Série</span>
                <p className="font-medium">{parsedData?.ide?.serie || data.invoice.invoice_series || '1'}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Data Emissão</span>
                <p className="font-medium">
                  {parsedData?.ide?.dhEmi 
                    ? new Date(parsedData.ide.dhEmi + 'T00:00:00').toLocaleDateString('pt-BR')
                    : data.invoice.emission_date 
                      ? new Date(data.invoice.emission_date).toLocaleDateString('pt-BR')
                      : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Natureza</span>
                <p className="font-medium text-sm">{parsedData?.ide?.natOp || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Totais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Produtos</span>
                <p className="font-medium">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                    .format(parseFloat(parsedData?.total?.ICMSTot?.vProd || '0'))}
                </p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Frete</span>
                <p className="font-medium">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                    .format(parseFloat(parsedData?.total?.ICMSTot?.vFrete || '0'))}
                </p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Desconto</span>
                <p className="font-medium">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                    .format(parseFloat(parsedData?.total?.ICMSTot?.vDesc || '0'))}
                </p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Total NF</span>
                <p className="font-medium text-lg text-primary">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                    .format(getTotalValue())}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items Preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Itens da Nota ({getItemsCount()})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2">Produto</th>
                  <th className="text-right p-2">Qtd</th>
                  <th className="text-right p-2">Valor Unit.</th>
                  <th className="text-right p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {parsedData?.det?.map((item: any, index: number) => {
                  const prod = item.prod || {};
                  return (
                    <tr key={index} className="border-b">
                      <td className="p-2">
                        <div className="font-medium truncate max-w-[300px]">
                          {prod.xProd}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Cód: {prod.cProd} | NCM: {prod.NCM}
                        </div>
                      </td>
                      <td className="text-right p-2">
                        {prod.qCom} {prod.uCom}
                      </td>
                      <td className="text-right p-2">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                          .format(parseFloat(prod.vUnCom || '0'))}
                      </td>
                      <td className="text-right p-2 font-medium">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                          .format(parseFloat(prod.vProd || '0'))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Info */}
      {(payments || dues) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Informações de Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {payments && (
                <div>
                  <span className="text-sm text-muted-foreground">Formas de Pagamento</span>
                  <div className="mt-1 space-y-1">
                    {payments.map((p: any, i: number) => (
                      <div key={i} className="flex justify-between">
                        <span>{p.type}</span>
                        <span className="font-medium">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                            .format(p.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {dues && (
                <div>
                  <span className="text-sm text-muted-foreground">Duplicatas</span>
                  <div className="mt-1 space-y-1">
                    {dues.map((d: any, i: number) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>
                          {d.number} - {d.dueDate ? new Date(d.dueDate + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                        </span>
                        <span className="font-medium">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                            .format(d.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
